import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useFormik } from 'formik';

import Loading from '../../components/Loading';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { Form } from '../../components/Form';
import { FormField } from '../../components/FormField';
import { Input } from '../../components/Input';
import { CustomSelect } from '../../components/Shared/CustomSelect';
import Button from '../../components/Button/Button';
import { Roles } from './Roles';

import { useGetUser, useEditUser, useGetUserSettings, useCreateUser } from '../../network';
import { Modal } from '../../components/Modal';
import { useState } from 'react';
import { useDeleteUser } from '../../network/queries/Users/useDeleteUser';

export const UserCreateEditPage = () => {
    const { t } = useTranslation();
    const { slug } = useParams();
    const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);

    const { data = {}, isInitialLoading } = useGetUser({ slug });
    const { data: userSettings = {} } = useGetUserSettings({ slug });

    const { mutate: editUser, isLoading: isEditing } = useEditUser();
    const { mutate: createUser, isLoading: isCreating } = useCreateUser();
    const { mutate: deleteUser, isLoading: isDeleting } = useDeleteUser();

    const user = data.user || {};
    const stores = (slug ? data.stores : userSettings.stores) || [];
    const roles = (slug ? data.roles : userSettings.roles) || [];

    const validationSchema = yup.object().shape({
        name: yup.string().required(t('Required!')),
        email: yup.string().required(t('Required!')),
    });

    const { handleChange, handleSubmit, setFieldValue, errors, values } = useFormik({
        initialValues: {
            name: user.name ?? '',
            email: user.email ?? '',
            role_id: user.role_id ?? '',
            restaurant_id: user.restaurant_id ?? '',
        },
        enableReinitialize: true,
        validateOnChange: false,
        validateOnBlur: false,
        validationSchema: validationSchema,
        onSubmit: (values) => {
            if (slug) {
                editUser({ id: slug, params: { ...values } });
            } else {
                createUser({ params: values });
            }
        },
    });

    const { name, email, restaurant_id, role_id } = values;

    const pages = [
        { name: t('Teams'), path: '/chain/users' },
        {
            name: slug ? user.name : t('Add user'),
            path: `/chain/stores/${slug ? user.id : 'create'}`,
        },
    ];

    // store select options
    const storesOptions = [
        { value: '', label: t('All stores') },
        ...stores.map((res) => ({
            label: res.name,
            value: res.id,
        })),
    ];

    const defaultStore = storesOptions.find((store) => store.value === restaurant_id) || null;
    const defaultRole = roles.find((store) => store.id === role_id) || null;

    const onDeleteUser = () => {
        deleteUser({ id: slug });
        setIsDeleteModalOpened(false);
    };

    return (
        <Loading loading={isInitialLoading || isDeleting}>
            <Breadcrumbs pages={pages} />

            <div className="max-w-screen-sm rounded-xl shadow-lg flex items-center p-6 mb-5">
                <Form handleSubmit={handleSubmit} className="w-full">
                    <div className="grid flex flex-col gap-4">
                        <FormField label={t('Name')} htmlFor="name" className="col-span-6" required error={errors.name}>
                            <Input id="name" type="text" name="name" onChange={handleChange} value={name} placeholder={t('Name')} />
                        </FormField>
                        <FormField label={t('Email')} disabled={slug} className="col-span-6" htmlFor="email" required error={errors.email}>
                            <Input id="email" type="text" disabled={slug} name="email" onChange={handleChange} value={email} placeholder={t('Email')} className="disabled:opacity-50" />
                        </FormField>
                        <FormField label={t('Locations')} className="col-span-6" error={errors.restaurant_id}>
                            <CustomSelect isDisabled={isInitialLoading} options={storesOptions} className="w-full mt-1" onChange={({ value }) => setFieldValue('restaurant_id', value)} value={defaultStore} placeholder={t('All')} />
                        </FormField>
                        <FormField label={t('User Role')} className="col-span-6 mt-2" required labelStyle="text-base font-medium text-gray-900">
                            <Roles roles={roles} defaultRole={defaultRole} setFieldValue={setFieldValue} />
                        </FormField>
                    </div>
                    <div className="flex gap-6 items-center justify-end mt-7">
                        {!!slug && (
                            <Button type="button" isUpperCase={false} disabled={isEditing || isCreating} color="bg-danger" className="h-9" onClick={() => setIsDeleteModalOpened(true)}>
                                {t('Delete')}
                            </Button>
                        )}
                        <Button type="submit" isUpperCase={false} isLoading={isEditing || isCreating} disabled={isEditing || isCreating} className="h-9 ">
                            {slug ? t('Edit') : t('Add')}
                        </Button>
                    </div>
                </Form>
                <Modal setIsOpen={setIsDeleteModalOpened} isOpen={isDeleteModalOpened}>
                    <h1 className="text-2xl">Are you sure you want to delete this user?</h1>
                    <div className="flex gap-6 mt-5 justify-end">
                        <Button onClick={onDeleteUser} type="button" isUpperCase={false} className="h-9" isLoading={isDeleting} color="bg-danger">
                            {t('Delete')}
                        </Button>
                        <Button type="button" onClick={() => setIsDeleteModalOpened(true)} className="h-9" disabled={isDeleting}>
                            {t('Close')}
                        </Button>
                    </div>
                </Modal>
            </div>
        </Loading>
    );
};
